import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Award, MapPin, Users, TrendingUp, Target, Lightbulb } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>About Arthaa Build | Building Purpose, Building Prosperity — Chandigarh Construction Company</title>
        <meta name="description" content="Arthaa Build — meaning purpose, wealth, substance in Sanskrit. Founded 2026 in Chandigarh with 10+ years of brand leasing expertise. Precast concrete construction, brand consultancy, and IT-powered real estate." />
        <link rel="canonical" href="https://arthaabuild.com/about" />
      </Helmet>

      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-widest text-[#7EB3D8] font-semibold mb-4">About Arthaa Build</p>
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-6">
              Building Purpose.<br />Building Prosperity.
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Founded in 2026, Arthaa Build is bringing revolutionary precast concrete technology and integrated real estate solutions to North India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Arthaa Philosophy */}
      <section className="section-padding bg-slate-50" data-testid="arthaa-philosophy">
        <div className="max-w-5xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-sm uppercase tracking-widest text-[#2B5278] font-semibold mb-3">The Name</p>
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">Why "Arthaa"?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              <strong>"Arthaa"</strong> (अर्थ) is a Sanskrit word with layered meaning — it embodies everything we build for.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { word: 'Meaning', sanskrit: 'अर्थ', desc: 'Everything built has intent and purpose behind it' },
              { word: 'Wealth', sanskrit: 'समृद्धि', desc: 'Building financial value and prosperity for every client' },
              { word: 'Substance', sanskrit: 'सार', desc: 'What truly matters — built into every structure' },
              { word: 'Goal', sanskrit: 'लक्ष्य', desc: 'Purposeful construction, not just building for the sake of it' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-xl border border-slate-200 text-center hover:border-[#2B5278]/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-3xl font-display text-[#2B5278]/80 mb-2">{item.sanskrit}</div>
                <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">{item.word}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-lg text-slate-700">
              <strong>"Build"</strong> grounds it in action — construction, growth, creation.<br />
              Together, <strong className="text-[#2B5278]">Arthaa Build</strong> = <em>Building with Purpose. Building Prosperity.</em>
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#2B5278]/10 rounded-xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-[#2B5278]" />
                </div>
                <h2 className="font-serif font-bold text-3xl text-slate-900">Our Vision</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                To become North India's most trusted name in technology-driven construction and real estate development—where engineering excellence meets intelligent systems to create high-quality, future-ready infrastructure.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We envision a construction industry transformed by precision manufacturing, reduced environmental impact, and accelerated project timelines that benefit developers, investors, and end-users alike.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#2B5278]/10 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-8 h-8 text-[#2B5278]" />
                </div>
                <h2 className="font-serif font-bold text-3xl text-slate-900">Our Motivation</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Traditional construction in India faces persistent challenges: delayed timelines, unpredictable quality, high labor dependency, and cost overruns. We founded Arthaa Build to change this.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                By combining factory-controlled precast manufacturing with IT-enabled project management and a decade of real estate expertise, we're bringing a revolutionary approach that saves time, reduces costs, and delivers superior quality—every single time.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Bring */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">What We Bring</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              A unique combination of construction technology, real estate expertise, and business intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="luxury-card bg-white p-8 rounded-xl text-center"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mx-auto mb-4">
                <Award className="w-7 h-7" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Engineering Excellence</h3>
              <p className="text-sm text-slate-600">Mechatronics engineering background with structural expertise</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="luxury-card bg-white p-8 rounded-xl text-center"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mx-auto mb-4">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">10+ Years Real Estate</h3>
              <p className="text-sm text-slate-600">Brand leasing & consultancy with 50+ partnerships</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="luxury-card bg-white p-8 rounded-xl text-center"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mx-auto mb-4">
                <Award className="w-7 h-7" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Certified Specialists</h3>
              <p className="text-sm text-slate-600">Modular Pulse certified precast construction experts</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="luxury-card bg-white p-8 rounded-xl text-center"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mx-auto mb-4">
                <MapPin className="w-7 h-7" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Regional Expertise</h3>
              <p className="text-sm text-slate-600">Deep North India market knowledge and networks</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facility */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif font-bold text-4xl text-slate-900 mb-6">
                Chandigarh Manufacturing Facility
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our state-of-the-art precast manufacturing facility in Chandigarh is being developed to serve Punjab, Haryana, Delhi NCR, Uttar Pradesh, and Himachal Pradesh markets.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Equipped with modern casting beds, steam curing chambers, and quality control systems—we're setting new standards for precision and speed in Indian construction.
              </p>
              <div className="bg-[#2B5278]/5 border-l-4 border-[#2B5278] p-6 rounded-r-lg">
                <p className="font-semibold text-slate-900 mb-2">Early Project Inquiries Welcome</p>
                <p className="text-slate-600">We're accepting project discussions and LOIs. Be among our first clients and benefit from priority scheduling and special engagement terms.</p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581094271901-8022df4466f9?q=80&w=2070" 
                alt="Manufacturing Facility"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">Comprehensive Solutions</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              End-to-end real estate services combining construction technology with business intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              'Precast Concrete Manufacturing',
              'Façade Engineering',
              'MEP Solutions',
              'High-Quality Finishing',
              'Brand Leasing Consultancy',
              'Property Management (Coming Soon)'
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="luxury-card bg-white p-6 rounded-lg"
              >
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{service}</h3>
                <div className="w-12 h-1 bg-[#2B5278] rounded-full"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl mb-6">Join Us in Transforming Construction</h2>
          <p className="text-xl text-white/90 mb-8">
            Whether you're a developer, contractor, or investor—partner with us to build faster, smarter, and better.
          </p>
          <Link to="/contact" className="inline-block bg-white text-[#2B5278] hover:bg-slate-50 font-semibold px-10 py-4 rounded-md transition-all">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;